// Components==============
import { useState } from 'react';
import { evidenceCollectionType } from 'typings';
import { getDate } from 'utilities/getDate';
import { useRedux } from './useRedux';
// =========================

export function useRecentActivities(
  evidence?: evidenceCollectionType[]
): RecentActivitiesResult {
  const userId = useRedux((state) => state.authentication?.user?.id);

  const evidenceIdList = evidence?.map((e) => e.evidence.id);

  const invites = useRedux((state) =>
    state.projectGroup?.entities.allIds
      .map((id) => state.projectGroup?.entities.byId[id])
      .filter((group) => group?.membership === 'invited')
  );

  const reflectionInvites = useRedux((state) =>
    state.reflection?.entities.allIds
      .map((id) => state.reflection?.entities.byId[id])
      ?.filter(
        (reflection) =>
          reflection?.user.id === userId &&
          !!reflection?.for_user &&
          !reflection?.finished
      )
  );

  const feedback = useRedux((state) =>
    state.feedback?.entities.allIds
      .map((id) => state.feedback?.entities.byId[id])
      .filter((f) => f?.author.id !== userId)
      .filter((f) => f?.evidence_id && evidenceIdList?.includes(f?.evidence_id))
  );

  // we need to use the below to notify the user when they have been invited to assess an assessment, if the userId equals the first_assessor_id then it needs to include that assessment
  // if the userId equals the second_assessor_id then it needs to include that assessment
  const assessmentInvites = useRedux((state) =>
    state.assessment?.entities.allIds
      .map((id) => state.assessment?.entities.byId[id])
      .filter((a) => {
        if (a?.first_assessor_id === userId) {
          return a?.first_assessor_id === userId;
        }
        if (a?.second_assessor_id === userId) {
          return a?.second_assessor_id === userId;
        }
        return false;
      })
  );

  // used for testing purposes TODO: send invites to notifications
  const [assessmentNotifications, setAssessmentNotifications] = useState<
    AssessmentEntity[]
  >(assessmentInvites || []);

  function clearAssessmentNotifications(id: number) {
    setAssessmentNotifications(
      assessmentNotifications.filter((a) => a.id !== id)
    );
  }

  const notifications = useRedux((state) => {
    const entities = state.notification?.entities.allIds.map(
      (id) => state.notification?.entities.byId[id]
    );

    const usedNotifications: number[] = [];

    return entities
      ?.map((n) => {
        if (!n?.target_id) return undefined;

        const projectId = n.project_id
          ? n.project_id
          : n.target
          ? 'project_id' in n.target
            ? n.target.project_id
            : n.target.id
          : 0;

        const themeId =
          state.project?.entities.byId?.[projectId]?.theme_id || 0;

        const theme = state.theme?.entities.byId?.[themeId];

        if (!theme) return undefined;

        if (usedNotifications.includes(n?.target_id)) {
          usedNotifications.push(n?.target_id);
          return undefined;
        }

        usedNotifications.push(n?.target_id);

        return {
          ...n,
          count: entities.filter((no) => no?.target_id === n.target_id).length,
        };
      })
      .filter((exists) => exists);
  })?.reverse();

  const activities = [
    ...(feedback || []),
    ...(invites || []),
    ...(reflectionInvites || []),
    ...(notifications || []),
    ...(assessmentNotifications || []),
  ]
    .map((activity) => {
      if (activity && 'membership' in activity) {
        return { type: 'invite', activity } as {
          type: 'invite';
          activity: ProjectGroupEntity;
        };
      }

      if (activity && 'emotions' in activity) {
        return { type: 'reflection-invite', activity } as {
          type: 'reflection-invite';
          activity: ReflectionEntity;
        };
      }

      if (activity && 'evidence_id' in activity) {
        return { type: 'feedback', activity } as {
          type: 'feedback';
          activity: FeedbackEntity;
        };
      }

      if (activity && 'target' in activity) {
        return { type: 'notification', activity } as {
          type: 'notification';
          activity: NotificationEntity;
        };
      }

      if (activity && 'first_assessor_id' in activity) {
        return { type: 'assessment', activity } as {
          type: 'assessment';
          activity: AssessmentEntity;
        };
      }

      return undefined;
    })
    .filter((exists) => exists)
    ?.sort(
      (a, b) =>
        getDate(b?.activity?.updated_at) - getDate(a?.activity?.updated_at)
    );

  return {
    activities,
    clearAssessmentNotifications,
  };
}
